import EntityLayer from './EntityLayer';
import { bind } from 'app/utils/decorators/decoratorUtils';
import { Vector as VectorLayer } from 'ol/layer';
import GeoJSON from 'ol/format/GeoJSON';
import GeometryCollection from 'ol/geom/GeometryCollection';
import { Fill, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import Point from 'ol/geom/Point';
import { stringDivider } from 'app/components/molecules/Map/EntityPin/entityPinStyle';
import { convertToSingleFeature } from 'app/components/molecules/Map/DrawShapes/DrawDrawingLayer';
import { isEmpty } from 'app/utils/utils';
import { getLayerZIndex, calculateSubLayerZIndex } from 'app/config/layerZIndexConfig';
import { LAYERS } from 'app/utils/maps/layer/layerUtils';

class DrawingLayer extends EntityLayer {
    constructor(info, map) {
        super(info, map);
        const { id } = info || {};
        const features = info?.drawing_feature || {};
        let _features = (!isEmpty(features) && JSON.parse(features)) || [];
        if (_features?.type === 'FeatureCollection') _features = _features?.features;
        _features.length && this.addDrawingToLayer(id, _features, map);
    }

    async setLayer() {
        const { name, filters, type, id, visible, opacity } = this.info || {};
        const { zIndex } = this.info || {};
        
        const calculatedZIndex = getLayerZIndex(zIndex, this.map, LAYERS.drawing);
        
        this.layer = new VectorLayer({
            name: id,
            title: name,
            visible,
            opacity,
            type,
            attributes: {
                id,
                type,
                subtitle: 'drawings',
                layerType: 'Drawing Layer',
                iconName: 'gesture',
                filters,
                noLocationPins: [],
            },
            source: this.getDataSource(),
            style: this.styleFunction,
            zIndex: calculatedZIndex,
        });
        
        
        await this.addLayerToMap();
    }

    @bind
    loader(extent, resolution, projection) {
        // this. getFeatureUnderExtent(extent)
    }

    styleFunction(feature, resolution) {
        const { fillColor, strokeColor, strokeWidth, geometry, properties } = feature.values_;
        if (geometry instanceof GeometryCollection) {
            const { geometries_ } = geometry;
            return geometries_.map((g, i) => {
                const { fillColor, strokeColor, strokeWidth } = properties[i] || {};
                return new Style({
                    geometry: g,
                    fill: new Fill({
                        color: fillColor || 'rgba(61, 100, 183, 0.3)',
                    }),
                    stroke: new Stroke({
                        color: strokeColor || 'green',
                        width: strokeWidth || 2,
                    }),
                });
            });
        }
        if (geometry instanceof Point) {
            const { fontSizeText, fontTypeText, drawingText, color, textBoxWidth } = feature?.values_;
            const font = `normal ${fontSizeText}px ${fontTypeText}`;
            return new Style({
                image: new CircleStyle({
                    radius: 0,
                    stroke: new Stroke({ color: 'red', width: 1 }),
                }),
                text: new Text({
                    font: `normal ${fontSizeText}px ${fontTypeText}`,
                    textAlign: 'center',
                    text: stringDivider(drawingText, textBoxWidth || 100, '\n', font) || '',
                    fill: new Fill({ color: color || 'red' }),
                }),
            });
        }
        return new Style({
            fill: new Fill({
                color: fillColor || 'rgba(61, 100, 183, 0.3)',
            }),
            stroke: new Stroke({
                color: strokeColor || 'green',
                width: strokeWidth || 2,
            }),
        });
    }

    addDrawingToLayer(id, features, map) {
        // Get the parent layer's z-index to calculate feature z-indices
        const parentZIndex = this.layer?.getZIndex() || 5;
        
        features.length &&
            features.forEach(item => {
                // in case of geometry collection, error will be handled by try catch.
                try {
                    let feature = new GeoJSON().readFeatures(item);
                    if (feature.length > 1) {
                        feature = convertToSingleFeature(feature);
                    }
                    
                    // Set z-index for the feature using calculateSubLayerZIndex
                    // This ensures consistency with the z-index system defined in layerZIndexConfig.js
                    if (feature?.[0]) {
                        // Use calculateSubLayerZIndex with 'entity' type for drawing features
                        const featureZIndex = calculateSubLayerZIndex(parentZIndex, 'entity');
                        feature[0].set('zIndex', featureZIndex);
                        
                      
                    }
                    
                    const layerSource = this.layer?.getSource(id, map);
                    layerSource && feature && layerSource.addFeature(feature?.[0]);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('error in geom', e);
                }
            });
        map.getMap().render();
    }

    getSource(id, map) {
        const foundLayer = id && map.findLayerById(this.layerId);
        foundLayer && foundLayer.getSource().clear();
        return foundLayer && foundLayer.getSource() && foundLayer.getSource();
    }
    
    /**
     * Updates the layer properties and propagates z-index changes to features
     * @param {Object} options - Layer update options
     * @param {number} options.zIndex - New z-index for the layer
     * @param {boolean} options.visible - Visibility state
     */
    updateLayer(options = {}) {
        const { zIndex, visible } = options;
        const layer = this.layer;
        const source = layer?.getSource?.();
        const features = source?.getFeatures?.() || [];
    
        if (!layer) return;
    
        if (typeof zIndex === 'number') {
            layer.setZIndex(zIndex);
    
            const featureZIndex = calculateSubLayerZIndex(zIndex, 'entity');
            features.forEach(feature => {
                feature.set('zIndex', featureZIndex);
            });
        }
    
        if (typeof visible === 'boolean') {
            layer.setVisible(visible);
    
            features.forEach(feature => {
                feature.set('visible', visible);
            });
        }
    
        layer.changed();
    }
}

export default DrawingLayer;
